import { LoaderContext } from 'context/LoaderContextProvider';
import { useContext } from 'react';
import { useNetwork } from 'services/network';

const devUrl = 'https://salonemilano.nautes.eu';

const useBookmarks = () => {
  const network = useNetwork();
  const { setLoaderActive } = useContext(LoaderContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const apiBaseUrl =
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
      ? devUrl
      : network.getBaseUrl(false);

  // Log Error util
  const logApiError = (err, request) => {
    let errorMessage = `🔴 API ERROR`;

    if (err.statusCode) errorMessage += ` ${err.statusCode}`;
    if (request) errorMessage += ` | ${request}`;
    if (err.message) errorMessage += `: ${err.message}`;

    console.error(errorMessage);
  };

  const listExhibitors = async ({
    useLoader = true,
    setData,
    salesforceId,
    status = '',
  }) => {
    try {
      useLoader && showLoader();
      const endpoint = status ? 'get-by-status' : 'get-bookmarked';
      const res = await network.drupalCustomApiLambda({
        method: 'get',
        url: `${apiBaseUrl}/api/sdm-exhibitor/exhibitors/${endpoint}/react/${salesforceId}${
          status ? `?status=${status}` : ''
        }`,
      });
      if (res && !res.content) {
        setData?.(res);
        return res;
      } else {
        return [];
      }
    } catch (error) {
      logApiError(error, 'list-bookmarked-exhibitors');
    } finally {
      useLoader && hideLoader();
    }
  };

  const saveExhibitors = async ({
    useLoader = false,
    salesforceId,
    exhibitorsIds = [],
    status = '',
  }) => {
    if (!exhibitorsIds?.length || !status) return null;
    try {
      useLoader && showLoader();
      return await network.drupalCustomApiLambda({
        method: 'post',
        url: `${apiBaseUrl}/api/sdm-exhibitor/exhibitors/set-visit`,
        body: {
          source: 'react',
          delta: salesforceId,
          exhibitorIds: exhibitorsIds.join(','),
          status: status,
        },
      });
    } catch (error) {
      logApiError(error, 'list-bookmarked-exhibitors');
    } finally {
      useLoader && hideLoader();
    }
  };

  // Folders Api Call
  const listFolders = async ({ useLoader = true, setData, salesforceId }) => {
    try {
      useLoader && showLoader();
      const res = await network.drupalCustomApiLambda({
        method: 'get',
        url: `${apiBaseUrl}/api/sdm-exhibitor/folders/get/react/${salesforceId}`,
      });
      if (res && !res.content) {
        setData?.(res);
        return res;
      } else {
        return 0;
      }
    } catch (error) {
      logApiError(error, 'list-folders');
    } finally {
      useLoader && hideLoader();
    }
  };

  const createFolder = async ({
    useLoader = false,
    salesforceId,
    folderName,
  }) => {
    if (!folderName || !salesforceId) return null;
    try {
      useLoader && showLoader();
      return await network.drupalCustomApiLambda({
        method: 'post',
        url: `${apiBaseUrl}/api/sdm-exhibitor/folders/create`,
        body: {
          source: 'react',
          delta: salesforceId,
          folderName: folderName,
          type: 'schedule',
        },
      });
    } catch (error) {
      logApiError(error, 'create-folder');
    } finally {
      useLoader && hideLoader();
    }
  };

  const renameFolder = async ({
    useLoader = false,
    salesforceId,
    folderName,
    folderId,
  }) => {
    if (!folderName || !folderId || !salesforceId) return null;
    try {
      useLoader && showLoader();
      return await network.drupalCustomApiLambda({
        method: 'patch',
        url: `${apiBaseUrl}/api/sdm-exhibitor/folders/rename/react/${salesforceId}`,
        body: {
          folderName: folderName,
          folderId: folderId,
        },
      });
    } catch (error) {
      logApiError(error, 'rename-folder');
    } finally {
      useLoader && hideLoader();
    }
  };

  const deleteFolder = async ({
    useLoader = false,
    salesforceId,
    folderId,
  }) => {
    if (!folderId || !salesforceId) return null;
    try {
      useLoader && showLoader();
      return await network.drupalCustomApiLambda({
        method: 'delete',
        url: `${apiBaseUrl}/api/sdm-exhibitor/folders/delete/react/${salesforceId}`,
        body: {
          folderId: folderId,
        },
      });
    } catch (error) {
      logApiError(error, 'delete-folder');
    } finally {
      useLoader && hideLoader();
    }
  };

  const deleteExhibitorsFromFolder = async ({
    useLoader = false,
    salesforceId,
    exhibitorsIds = [],
    folderId,
  }) => {
    if (!folderId || !salesforceId || !exhibitorsIds.length) return null;
    try {
      useLoader && showLoader();
      return await network.drupalCustomApiLambda({
        method: 'delete',
        url: `${apiBaseUrl}/api/sdm-exhibitor/exhibitors/remove-from-folder/react/${salesforceId}`,
        body: {
          folderId: folderId,
          exhibitorIds: exhibitorsIds.join(','),
        },
      });
    } catch (error) {
      logApiError(error, 'delete-folder');
    } finally {
      useLoader && hideLoader();
    }
  };

  const moveExhibitorsToFolder = async ({
    useLoader = false,
    salesforceId,
    exhibitorsIds = [],
    newFolderId,
    toBeRemoved = [],
  }) => {
    if (!newFolderId || !exhibitorsIds?.length || !salesforceId) return null;
    try {
      useLoader && showLoader();
      const resMove = await network.drupalCustomApiLambda({
        method: 'patch',
        url: `${apiBaseUrl}/api/sdm-exhibitor/exhibitors/move-in-folder/react/${salesforceId}`,
        body: {
          exhibitorIds: exhibitorsIds.join(','),
          folderId: newFolderId,
        },
      });
      if (toBeRemoved?.length && resMove) {
        const resDelete = await Promise.all(
          toBeRemoved.map((obj) => {
            const folderId = obj.folderId;
            const exhibitorsIds = obj.exhibitorsIds;
            return deleteExhibitorsFromFolder({
              useLoader: false,
              folderId,
              exhibitorsIds,
              salesforceId,
            });
          })
        );
        if (resDelete?.length) return true;
      } else {
        return resMove;
      }
    } catch (error) {
      logApiError(error, 'move-exhibitor');
    } finally {
      useLoader && hideLoader();
    }
  };

  return {
    listExhibitors,
    saveExhibitors,
    createFolder,
    renameFolder,
    deleteFolder,
    listFolders,
    moveExhibitorsToFolder,
    deleteExhibitorsFromFolder,
  };
};

export { useBookmarks };
